import get from 'lodash/get'
import startCase from 'lodash/startCase'

import { CURRENCIES } from 'configuration/currency'
import { type ConfigType, date } from 'configuration/dayjs'
import { SELLER_BRAND_TYPE } from 'const/brands'
import {
    DAY_FORMAT,
    MONTH_FORMAT,
    OTHER,
    UNDEFINED_VALUE,
} from 'const/formatting'
import {
    BROAD,
    EXACT,
    NEGATIVE_EXACT,
    NEGATIVE_PHRASE,
    PHRASE,
} from 'const/matchTypes'
import numeral from 'utilities/numeral'

import { isUnset } from './utilities'

type UnformattedValue = string | number | null | undefined

export const titleCase = (value: unknown): string => {
    if (typeof value !== 'string') {
        return `${value}`
    }
    return value ? startCase(value.toLowerCase()) : ''
}

/**
 *
 * ------------ DATE FORMATTING --------------- /
 *
 */

export const HC_DATE_FORMAT = {
    hour: '%l%P',
    day: '%b %e',
    week: '%b %e',
    month: "%b '%y",
    year: '%Y',
    full: '%b %e %H:%M',
}

export const formatCerebroDate = (value: ConfigType): string =>
    value ? date(value).format('YYYY-MM-DD') : ''

export const formatJSONDateTime = (value: ConfigType): string =>
    value ? date(value).format('YYYY-MM-DDTHH:mm') : ''

export const formatCerebroDateTime = (value: ConfigType): string =>
    value
        ? date(value).local().format('MM/DD/YYYY, h:mm:ss a')
        : UNDEFINED_VALUE

export const formatDay = (value: ConfigType): string =>
    value ? date(value).format(DAY_FORMAT) : UNDEFINED_VALUE

export const formatDayofWeek = (value: unknown): string => {
    if (isUnset(value)) {
        return UNDEFINED_VALUE
    }
    const dayNumber = parseInt(String(value), 10) % 7
    if (dayNumber < 0) {
        return OTHER
    }
    return date().day(dayNumber).format('dddd')
}

export const formatDayOfWeekFromRecord = (
    record: object,
    path: string
): string => formatDayofWeek(get(record, path))

export const formatQuarter = (value: unknown): string => {
    if (isUnset(value)) {
        return UNDEFINED_VALUE
    }
    const quarterNumber = parseInt(String(value), 10) % 4
    if (quarterNumber < 0) {
        return OTHER
    }
    return numeral(quarterNumber === 0 ? 4 : quarterNumber).format('0o')
}

export const formatHourOfDay = (militaryTimeHour: unknown): string => {
    if (isUnset(militaryTimeHour)) {
        return UNDEFINED_VALUE
    }
    const hourNumber = parseInt(String(militaryTimeHour), 10) % 24
    if (hourNumber < 0) {
        return OTHER
    }
    return date().hour(hourNumber).startOf('hour').format('ha')
}

export const formatHourOfDayFromRecord = (
    record: object,
    path: string
): string => formatHourOfDay(get(record, path))

export const formatMonthName = (value: unknown): string => {
    if (isUnset(value)) {
        return UNDEFINED_VALUE
    }
    const monthNumber = (parseInt(String(value), 10) - 1) % 12
    if (monthNumber < 0) {
        return OTHER
    }
    return date().month(monthNumber).format('MMMM')
}

const formatMonthYear = (value: unknown): string => {
    if (isUnset(value)) {
        return OTHER
    }
    return date(value as ConfigType).format(MONTH_FORMAT)
}

export const formatMonthNameFromRecord = (
    record: object,
    path: string
): string => formatMonthName(get(record, path))

export const formatMonthYearNameFromRecord = (
    record: object,
    path: string
): string => formatMonthYear(get(record, path))

export const formatMatchType = (text: string): string => {
    switch (text) {
        case EXACT:
        case PHRASE:
        case BROAD:
            return titleCase(text)
        case NEGATIVE_EXACT:
        case NEGATIVE_PHRASE:
            return titleCase(text.replace('negative', 'Negative '))
        default:
            return titleCase(text)
    }
}

export const formatPercentage = (
    value: UnformattedValue,
    format = '0.00%'
): string => {
    if (isUnset(value) || Number.isNaN(value)) {
        return UNDEFINED_VALUE
    }
    return numeral(value).format(format)
}

// Runs `format` with numeral's locale temporarily switched to `currencyCode`,
// restoring the previous locale afterward. Every numeral currency locale shares
// the same delimiters and abbreviations (see utilities/numeral) and differs only
// in the currency symbol, so scoping changes only how `$` renders — safe to wrap
// around any metric formatting, currency or not. An empty/unknown code is a
// no-op, leaving today's active-global-locale behavior untouched. The closure
// must format synchronously; numeral.format runs immediately so this is the case
// for our renderers (including Highcharts formatter callbacks).
export function withCurrencyLocale<T>(
    currencyCode: string | null | undefined,
    format: () => T
): T {
    if (!currencyCode || !CURRENCIES[currencyCode]) {
        return format()
    }
    const originalLocale = numeral.options.currentLocale
    numeral.locale(currencyCode)
    try {
        return format()
    } finally {
        numeral.locale(originalLocale)
    }
}

// Captures numeral's active locale so a formatter invoked later (e.g. during a
// child element's own render, after withCurrencyLocale has restored the global
// locale) still formats with the locale that was active when it was created.
export function bindActiveCurrencyLocale<Args extends unknown[], Result>(
    format: (...args: Args) => Result
): (...args: Args) => Result {
    const boundLocale = numeral.options.currentLocale
    return (...args) => {
        const activeLocale = numeral.options.currentLocale
        if (activeLocale === boundLocale) {
            return format(...args)
        }
        numeral.locale(boundLocale)
        try {
            return format(...args)
        } finally {
            numeral.locale(activeLocale)
        }
    }
}

export interface FormatCurrencyOptions {
    decimal?: boolean
    abbreviate?: boolean
    currencyCode?: string
    // Explicit numeral format string; overrides the decimal/abbreviate presets.
    format?: string
}

export const formatCurrency = (
    value: UnformattedValue,
    {
        decimal = false,
        abbreviate = false,
        currencyCode = '',
        format,
    }: FormatCurrencyOptions = {}
): string => {
    const originalLocale = numeral.options.currentLocale
    const currency = CURRENCIES[currencyCode]
    if (currency) {
        numeral.locale(currencyCode)
    }

    let returnValue: string
    if (format) {
        returnValue = numeral(value).format(format)
    } else if (decimal && abbreviate) {
        returnValue = numeral(value).format('$0.[00]a')
    } else if (decimal) {
        returnValue = numeral(value).format('$0,0.00')
    } else if (abbreviate) {
        returnValue = numeral(value).format('$0a')
    } else {
        returnValue = numeral(value).format('$0,0')
    }

    if (currency) {
        numeral.locale(originalLocale)
    } else {
        // Fallback for currencies we don't officially support
        return returnValue.replace('$', `${currencyCode}$`)
    }

    return returnValue
}

export const formatNumber = (
    value: UnformattedValue,
    format = '0,0',
    showUnsetAsNumber = true,
    convertToDecimalPercentage = false,
    emptyValue = UNDEFINED_VALUE
): string => {
    if (!showUnsetAsNumber && isUnset(value)) {
        return emptyValue
    }

    const num = numeral(value)
    if (convertToDecimalPercentage) {
        num.divide(100)
    }
    return num.format(format)
}

export const formatAbbreviatedMetric = (
    value: UnformattedValue,
    format: string,
    shortFormat = '',
    showUnsetAsNumber = true,
    convertToDecimalPercentage = false
): string => {
    const abbreviationThreshold = 1000
    if (!showUnsetAsNumber && isUnset(value)) {
        return UNDEFINED_VALUE
    }

    if (format === 'YYYY-MM-DD') {
        return formatCerebroDate(value)
    }

    if (format.includes('%') || shortFormat === '' || format === shortFormat) {
        return formatNumber(
            value,
            format,
            showUnsetAsNumber,
            convertToDecimalPercentage
        )
    }

    return numeral(value).format(
        Number(value) >= abbreviationThreshold ? shortFormat : format
    )
}

export const formatMultiplier = (
    multiplier: number | undefined,
    multiplierFormatPercent = false
): string => {
    if (isUnset(multiplier)) {
        return UNDEFINED_VALUE
    }
    if (multiplierFormatPercent) {
        return `${numeral(multiplier).format('%')}`
    }
    return `${numeral(multiplier).format('0.00')} x`
}

export interface AdAccountBrandLike {
    brand_name?: string | null
    country_code?: string | null
    brand_entity_id?: string | null
    type?: string | null
}

/**
 * @deprecated Use _formatAdAccountName_ instead.
 * @see formatAdAccountName
 */
export const formatBrandName = (
    {
        brand_name: brandName,
        country_code: countryCode,
        brand_entity_id: brandEntityId,
        type,
    }: AdAccountBrandLike,
    options: { appendCountry?: boolean } = { appendCountry: true }
): string => {
    let formattedBrandName: string

    if (brandName) {
        formattedBrandName = brandName
    } else if (type === SELLER_BRAND_TYPE) {
        formattedBrandName = `Seller Account ${brandEntityId ?? ''}`
    } else {
        formattedBrandName = brandEntityId ?? ''
    }

    if (options.appendCountry && countryCode) {
        formattedBrandName = `${formattedBrandName} (${countryCode})`
    }

    return formattedBrandName
}

/**
 * @todo Rename formatBrandName after removing all code references to it.
 * @see formatBrandName
 */
export const formatAdAccountName = (
    adAccount: AdAccountBrandLike,
    options: { appendCountry?: boolean } = { appendCountry: true }
): string => formatBrandName(adAccount, options)

export const formatUrl = (url: string): string => {
    if (!url.match(/^https?:\/\//)) {
        return `http://${url}`
    }
    return url
}

export interface SovKeywordLike {
    country_code?: string
    language_code?: string
    platform?: string
    text?: string
}

export const formatSovKeywordLabel = (keyword: SovKeywordLike): string => {
    const metadata = [
        keyword.country_code,
        keyword.language_code,
        titleCase(keyword.platform),
    ].join(' | ')
    return `${keyword.text} (${metadata})`
}

export const formatAccountType = (account_type: unknown): string => {
    if (isUnset(account_type)) {
        return UNDEFINED_VALUE
    }

    if (typeof account_type !== 'string') {
        return `${account_type}`
    }

    if (account_type.toUpperCase() === account_type) {
        return account_type
    }
    return titleCase(account_type)
}

export const getValueFormatter =
    (
        type: string | undefined,
        currencyCode: string,
        format: string | undefined
    ): ((value: UnformattedValue) => string) =>
    (value: UnformattedValue) => {
        if (isUnset(value)) {
            return UNDEFINED_VALUE
        }
        return type === 'currencyAmount'
            ? formatCurrency(Number(value), {
                  decimal: true,
                  abbreviate: true,
                  currencyCode,
              })
            : formatNumber(value, format)
    }
